// Angular
import {
	Component,
	OnInit,
	ComponentFactoryResolver,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { formatNumber, formatCurrency } from '@angular/common';
// Models
import { DOCUMENT_RULES_UI_SECTIONS, UISTATE } from '../../model/quality.constant';
import { RuleModel } from '../../rules/model/rule.model';
import { RuleEngine } from '../../rules/model/rule-engine';
import { PhotoReviewModel } from '../../photo-review/model/photo-review.model';
import { PTableCompGrid1073and1075 } from './model/p-table-comp-grid-1073-1075.layout';
import { PTableCompGridDataModel, PTableCompGridFormattedDataModel, PTableCompGridHeaderColumn } from './model/p-table-comp-grid-data.model';
import { PTableCompGrid1004And2055 } from './model/p-table-comp-grid-1004-2055.layout';
// PrimeNG
import { Table } from 'primeng/table';
// Core
import { BroadcastService, ObjectUtils, SubContentService } from '@cv-invision/core';
// ngxs
import { Store } from '@ngxs/store';
// rxjs
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Rules
import { RulesComponent, UI_FIELD_SELECTED_EVENT } from '../../rules/rules.component';
// State
import { RulesState } from '../../rules/state/rules.state';
import { PhotoReviewState } from '../../photo-review/state/photo-review.state';
import { DeleteSelectedRule } from '../../rules/state/action/rules.actions';
// Service
import { JPathService } from '@cv-invision/cv.component-library.ux';
import { CompGridService } from './service/comp-grid.service';

@Component({
	selector: 'cv-p-table-comp-grid',
	templateUrl: './p-table-comp-grid.component.html',
	styleUrls: ['./p-table-comp-grid.component.scss']
})
export class PTableCompGridComponent implements OnInit, OnDestroy {
	@ViewChild('pTable', { static: true }) pTable: Table;
	selectedRule$: Observable<any> = this.store.select(RulesState.getSelectedRule);
	photos$: Observable<Array<PhotoReviewModel>> = this.store.select(PhotoReviewState.getPhotoReviewCollection);
	rules$: Observable<any> = this.store.select(RulesState.getOrderRules);
	public document: any;
	public photos: Array<any> = [];
	public comparableRanks = [];
	public orderData: Array<PTableCompGridFormattedDataModel> = [];
	public cols: Array<PTableCompGridHeaderColumn> = [];
	public frozenCols: any;
	public frozenItems: Array<PTableCompGridFormattedDataModel> = [];
	public unFrozenItems: Array<PTableCompGridFormattedDataModel> = [];
	public rules: any;
	private tableLayout: Array<PTableCompGridDataModel> = [];
	private atComponentDestroy = new Subject();
	targets: any;
	overviewUiState: any;
	uiState: string;

	constructor(
		private subContentService: SubContentService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private store: Store,
		private broadcastService: BroadcastService,
		private compGridService: CompGridService,
		private jPathService: JPathService,
	) {
		this.targets = {
			data: [],
			primary: []
		};

		this.overviewUiState = UISTATE;

		this.photos$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((photos: Array<PhotoReviewModel>) => {
				if (photos) {
					this.photos = photos;
				}
			});

		this.rules$.pipe(takeUntil(this.atComponentDestroy)).subscribe((orderRules: any) => {
			this.rules = orderRules;
		});
	}

	ngOnInit(): void {
		RuleEngine.setSelectedSection(this.store, DOCUMENT_RULES_UI_SECTIONS.COMPS);
		this.uiState = UISTATE.IS_LOADING;
		this.store
			.select(s => s.quality)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(quality => {
				if (quality && quality.documentOrder && quality.photoreview) {
					const schemaName = quality.documentSchema.Metadata[0].DisplayText;

					if (schemaName === 'CV Review 1004' || schemaName === 'CV Review 2055') {
						this.tableLayout = PTableCompGrid1004And2055.sections;
					} else {
						this.tableLayout = PTableCompGrid1073and1075.sections;
					}

					if (this.uiState !== UISTATE.IS_READY) {
						this.document = quality.documentOrder;
						this.uiState = UISTATE.IS_READY;
						this.dataIsReady();
					}
				}
			});

		// After a rule has been selected if it belongs to the same section it will try to Highlight the Field
		this.selectedRule$.pipe(takeUntil(this.atComponentDestroy)).subscribe((selectedRule: any) => {
			if (selectedRule && RuleEngine.isCurrentUISection(selectedRule, DOCUMENT_RULES_UI_SECTIONS.COMPS)) {
				setTimeout(() => {
					this.getSelectedRule(selectedRule);
					// We Clean latest value
					this.store.dispatch(new DeleteSelectedRule());
				});
			}
		});
	}

	/**
	 * update p-table's scroll to default - (0,0)
	 */
	updateScroll() {
		this.pTable.scrollTo({ left: 0, top: 0 });
	}

	/**
	 * prepare data to the grid
	 */
	prepareData(): any {
		const newData: Array<PTableCompGridFormattedDataModel> = [];

		const formatNewValue = (type: string, obj: any): string => {
			const v = Object.prototype.hasOwnProperty.call(obj, 'Value')
			if (obj && v) {
				let currentValue = obj.Value || '';

				if (currentValue.trim() === 'Y') {
					currentValue = 'Yes';
				} else if (currentValue.trim() === 'N') {
					currentValue = 'No';
				}

				const prefix = (() => {
					if (currentValue && type) {
						if (type === 'money') {
							return '$';
						}

						if (type === 'percentage') {
							return '%';
						}
					}

					return '';
				})();

				if (prefix === '%') {
					return `${currentValue}${prefix}`;
				}

				if (prefix === '$') {
					let currencyValue = currentValue.replace('$', '');
					currencyValue =
						currentValue.indexOf('.') !== -1
							? parseFloat(currencyValue)
							: parseInt(currencyValue, 10);
					return `${formatCurrency(currencyValue, 'en_US', '$')}`;
				}

				if (currentValue && type === 'terrain') {
					const terrainValue = (() => {
						const containsAc = currentValue.toLowerCase().indexOf('ac') !== -1;
						const sizeValue =
							currentValue.indexOf('.') !== -1
								? parseFloat(currentValue)
								: parseInt(currentValue, 10);
						const sizeFormattedValue = formatNumber(sizeValue, 'en_US');

						if (containsAc) {
							return `${sizeFormattedValue} ac`;
						}

						if (sizeValue < 43560) {
							return `${sizeFormattedValue} Sq. Ft.`;
						}

						const sqToAcValue = (sizeValue / 43560).toFixed(2);
						return `${formatNumber(parseFloat(sqToAcValue), 'en_US')} ac`;
					})();

					return terrainValue;
				}

				return `${prefix}${currentValue}`;
			}
			return '';
		};

		this.tableLayout.forEach((item: PTableCompGridDataModel) => {
			const data: PTableCompGridFormattedDataModel = {
				key: item.key,
				headerName: item.headerName,
				values: [],
				type: item.type
			};

			if (this.comparableRanks.length < 1) {
				const path = [];
				let value: any;
				let obj: any;

				if (item.key === 'photo') {
					const photo = this.photos.find(p => p.groupName === 'Subject');
					value = photo && Object.prototype.hasOwnProperty.call(photo, 'path') ? photo.path : 'N/A';
				} else if (item.key === 'publicRecords') {
					value = 'Public Records';
				} else if (item.subjectPath) {
					obj = this.jPathService.getValue(this.document, `$.${item.subjectPath}`);
					try {
						path.push(obj.Path);
						obj = formatNewValue(item.type ? item.type : '', obj);
						value = obj && Object.prototype.hasOwnProperty.call(obj, 'Value') ? obj.Value : obj || '';

						if (item.key === 'yearBuilt') {
							if (Array.isArray(value) && value.length && value[0]) {
								value[0] = new Date().getFullYear() - parseInt(value[0], 10);
							} else if (value) {
								value = new Date().getFullYear() - parseInt(value, 10);
							}
						}
					} catch (error) {
						// tslint:disable-next-line: no-console
						// console.warn(obj, item.subjectPath);
					}
				}

				data.values?.push({
					value: value,
					columnKey: 'subject',
				});

			}

			this.comparableRanks.forEach((elem, index) => {
				let value: any;
				const path = [];
				const columnKey = index === 0 ? `subject` : `comp${index}`;

				if (item.key === 'photo') {
					const searchWord = index === 0 ? `Subject` : `Comparable#${index}`;
					const photo = this.photos.find(p => p.groupName === searchWord);
					value = photo && Object.prototype.hasOwnProperty.call(photo, 'path') ? photo.path : 'N/A';
				} else if (item.key === 'publicRecords') {
					value = 'Public Records';
				} else {
					let obj: any;
					if (index === 0 && item.subjectPath) {
						obj = this.jPathService.getValue(this.document, `$.${item.subjectPath}`);
						try {
							path.push(obj.Path);

							obj = formatNewValue(item.type ? item.type : '', obj);

						} catch (error) {
							// tslint:disable-next-line: no-console
							// console.warn(obj, item.subjectPath);
						}
					} else if (item.compPath && item.compPath.length) {
						obj = item.compPath.map((c, cIndex) => {
							if (elem[c] && Object.prototype.hasOwnProperty.call(elem[c], 'Value')) {
								path.push(elem[c].Path);
								return formatNewValue(cIndex > 0 ? 'money' : item.type, elem[c]);
							}
							return '';
						});
					}

					value = obj && Object.prototype.hasOwnProperty.call(obj, 'Value') ? obj.Value : obj || '';

					if (item.key === 'yearBuilt') {
						if (Array.isArray(value) && value.length && value[0]) {
							value[0] = new Date().getFullYear() - parseInt(value[0], 10);
						} else if (value) {
							value = new Date().getFullYear() - parseInt(value, 10);
						}
					}
				}

				let hasTwoValues = false;
				let isComma = false;
				if (Array.isArray(value) && value.length > 1) {
					let count = 0;
					value.forEach((val) => {

						if (typeof val === 'string' && val.trim().length === 0) {
							count++
						}
					})
					if (count === 2) {
						isComma = true;
					}
					if (count <= 1) {
						hasTwoValues = true;
					}
				}

				const hasRule = this.fieldHasRule(path);

				data.values?.push({
					value: isComma ? '' : value,
					columnKey: columnKey,
					isFrozenColumn: index === 0 ? true : false,
					hasTwoValues: hasTwoValues,
					hasRule: hasRule,
					path: path
				});
			});

			newData.push(data);
		});
		return newData;
	}

	/**
	 * Initialize all the data used by the component
	 * this method will be triggered once the data is loaded regarding refresh page
	 */
	dataIsReady(): void {
		// this.photos = this.store.selectSnapshot(PhotoReviewState.getPhotoReviewCollection);
		this.comparableRanks = this.jPathService.getQuery(this.document, `$.CV_EXTENSION_SECTION.FIELDS.COMPARABLE.*`);
		this.setHeaders();
		this.orderData = this.prepareData();
		const copiedData = ObjectUtils.recursiveDeepCopy(this.orderData);
		this.unFrozenItems = copiedData;
		this.frozenItems = this.unFrozenItems.splice(0, 3);
	}

	/**
	 * Select field
	 */
	onFieldSelected(path: Array<string>): void {
		if (path.length) {
			const rules = this.getFieldRules(path[0]);

			if (rules.length) {
				this.broadcastService.broadcast({
					name: UI_FIELD_SELECTED_EVENT,
					event: {
						rule: rules[0]
					}
				});
			}
		}
	}

	/**
	 * The field has a rule asigned
	 */
	fieldHasRule(path: Array<string>): boolean {
		if (path.length) {
			const rules = this.getFieldRules(path[0]);
			return rules.length > 0;
		}

		return false;
	}

	/**
	 * Return the rules asigned to the field
	 * @param fieldPath
	 */
	getFieldRules(fieldPath: string): Array<RuleModel> {
		const field = fieldPath.replace(/[^a-zA-Z ]/g, '').toLowerCase();
		const rules: Array<RuleModel> = this.rules.violation.filter(rule => {
			let isAvailable = false;
			if (rule.targets.length) {
				const targets = rule.targets.join().replace(/[^a-zA-Z ]/g, '').toLowerCase();
				isAvailable = targets.indexOf(field) !== -1;
			}
			return isAvailable;
		});

		return rules;
	}

	/**
	 * set column headers
	 */
	public setHeaders(): void {
		this.document.CV_EXTENSION_SECTION.FIELDS.COMPARABLE.forEach((item, index: number) => {
			if (index === 0) {
				this.cols.push({
					field: 'subject',
					header: 'Subject',
					isFrozenColumn: true,
				})
			} else {
				this.cols.push({
					field: 'comp' + index,
					header: 'Comp' + index,
					isFrozenColumn: false,
				})
			}
		})
	}

	/**
	 * get the rule selected
	 * @param item selected rule model
	 */
	getSelectedRule(item: RuleModel): void {
		const targets = [...item['primaryTargets'], ...item['targets']];
		this.compGridService.setRuleSelected({
			targets: targets,
			type: item['messageType'].toLocaleLowerCase()
		});
	}

	/**
	 * Load the comps  Rules through store managment and then open the Rules SubContent panel.
	 *
	 */
	private loadRules(): void {
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: RulesComponent,
				data: {
					uiSection: DOCUMENT_RULES_UI_SECTIONS.COMPS,
					targets: this.targets
				}
			})
			.subscribe(updateEvent => {
				this.getSelectedRule(updateEvent);
			});
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
